import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Stack } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionRequestTemplateVersion } from "../../../../api/types/dataCollectionTypes";
import CreateDataRequestTemplateDialog from "./CreateDataRequestTemplateDialog";
import DataRequestSetupPageContext from "./DataRequestSetupPageContext";
import DataRequestTemplatesGrid from "./DataRequestTemplatesGrid";

const DataRequestTemplatesView = () => {
  const { hasPermissionsToManage } = useDefinedContext(DataRequestSetupPageContext);

  const [searchTerm, setSearchTerm] = useState<string>("");

  const [templates, fetchTemplatesError, { setData: setTemplates }] = useFetch(adminApi.getDataRequestTemplates);

  const [isCreateTemplateDialogOpen, setIsCreateTemplateDialogOpen] = useState(false);

  if (fetchTemplatesError) {
    logError(fetchTemplatesError, "[DataRequestTemplatesView] getDataCollectionRequestTemplates");
    return <DataLoadingFailed title="Could not load data request templates" />;
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value.trim().toLowerCase());
  };

  const handleTemplateCreated = (template: DataCollectionRequestTemplateVersion) => {
    setIsCreateTemplateDialogOpen(false);
    setTemplates((prev) => [...(prev ?? []), template]);
  };

  const allTemplates = templates ?? [];

  const filteredTemplates = searchTerm
    ? allTemplates.filter((t) => t.name.toLowerCase().includes(searchTerm))
    : allTemplates;

  const templateNames = allTemplates.map((t) => t.name);

  return (
    <>
      <Stack spacing={2} sx={{ height: "100%", width: "100%", flex: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <RecordCounter records={filteredTemplates.length} totalRecords={allTemplates.length} />
          <Stack direction="row" spacing={1}>
            <SearchField initialValue={searchTerm} debounceTimeMs={300} onSearch={handleSearch} />
            {hasPermissionsToManage && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateTemplateDialogOpen(true)}>
                Add New
              </Button>
            )}
          </Stack>
        </Box>

        <DataRequestTemplatesGrid rows={filteredTemplates} isLoading={templates === undefined} />
      </Stack>
      {isCreateTemplateDialogOpen && (
        <CreateDataRequestTemplateDialog
          onClose={() => setIsCreateTemplateDialogOpen(false)}
          onCreated={handleTemplateCreated}
          existingTemplateNames={templateNames}
        />
      )}
    </>
  );
};

export default DataRequestTemplatesView;
